import React, { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, Clock, Send, Anchor, User, MessageSquare, Ship, Users, Calendar, FileText, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [cruises, setCruises] = useState([]);

  const [contactForm, setContactForm] = useState({
    name: '', email: '', phone: '', subject: '', message: '',
  });
  const [quoteForm, setQuoteForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    cruise_name: '', request_type: '', preferred_dates: '',
    passenger_count: '', message: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/cruises`).then(res => setCruises(res.data)).catch(() => {});
  }, []);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({ ...prev, [name]: value }));
  };
  const handleQuoteChange = (e) => {
    const { name, value } = e.target;
    setQuoteForm(prev => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      setSubmitted(true);
      setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error("Erreur lors de l'envoi", { description: 'Veuillez réessayer ou nous contacter par téléphone.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...quoteForm,
        passenger_count: parseInt(quoteForm.passenger_count) || 2,
      });
      setSubmitted(true);
      setQuoteForm({ customer_name: '', customer_email: '', customer_phone: '', cruise_name: '', request_type: '', preferred_dates: '', passenger_count: '', message: '' });
    } catch {
      toast.error("Erreur lors de l'envoi", { description: 'Veuillez réessayer ou nous contacter par téléphone.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Layout>
        <section className="pt-32 pb-24 bg-[#0A2540] min-h-[70vh] flex items-center">
          <div className="max-w-xl mx-auto px-4 text-center">
            <div className="w-20 h-20 bg-[#2A9D8F]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-[#2A9D8F]" />
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="contact-success-title">
              Message envoy&eacute; !
            </h1>
            <p className="text-white/70 text-lg mb-8">
              Merci pour votre message. Notre &eacute;quipe vous r&eacute;pondra dans les plus brefs d&eacute;lais, g&eacute;n&eacute;ralement sous 24h.
            </p>
            <Button
              onClick={() => setSubmitted(false)}
              className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white"
              data-testid="contact-send-another-btn"
            >
              Envoyer un autre message
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title="Contact & Devis personnalisé — Croisière catamaran SOGNUDIMARE"
        description="Contactez SOGNUDIMARE pour votre croisière catamaran en Corse, Sardaigne, Grèce, Caraïbes. Devis gratuit sous 24h. Tél. 04 95 72 90 28 — contact@sognudimare-catamarans.com."
        path="/contact"
        keywords={[
          'contact sognudimare',
          'devis croisière catamaran',
          'devis catamaran corse',
          'réservation catamaran ajaccio',
          'téléphone sognudimare',
          'agence croisière corse',
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact SOGNUDIMARE',
          url: 'https://sognudimare.com/contact',
          mainEntity: {
            '@type': 'TravelAgency',
            name: 'SOGNUDIMARE',
            telephone: '+33-4-95-72-90-28',
            email: 'contact@sognudimare-catamarans.com',
          },
        }}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#0A2540]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
            <Anchor className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-white/80 text-sm">Contactez-nous</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
            Parlons de votre <span className="italic text-[#D4AF37]">projet</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Une question, une demande de devis ou un projet sur mesure ? Notre &eacute;quipe est l&agrave; pour vous accompagner.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Info Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="bg-[#0A2540] text-white border-0">
                <CardContent className="p-6 space-y-6">
                  <h3 className="font-display text-xl font-semibold">Informations de contact</h3>
                  <div className="space-y-4">
                    <InfoRow icon={Phone} title="T&eacute;l&eacute;phone">
                      <a href="tel:0495729028" className="text-white/70 hover:text-[#D4AF37] transition-colors">04 95 72 90 28</a>
                    </InfoRow>
                    <InfoRow icon={Mail} title="Email">
                      <a href="mailto:contact@sognudimare-catamarans.com" className="text-white/70 hover:text-[#D4AF37] transition-colors break-all">contact@sognudimare-catamarans.com</a>
                    </InfoRow>
                    <InfoRow icon={Clock} title="Horaires">
                      Lun - Sam: 9h - 19h<br />Dimanche: Sur rendez-vous
                    </InfoRow>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Form Area */}
            <div className="lg:col-span-2">
              {/* Tab Switcher */}
              <div className="flex gap-2 mb-6" data-testid="contact-tabs">
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-all ${
                    activeTab === 'contact'
                      ? 'bg-[#0A2540] text-white shadow-lg'
                      : 'bg-white text-[#0A2540] hover:bg-[#0A2540]/5 border border-slate-200'
                  }`}
                  data-testid="tab-contact"
                >
                  <MessageSquare className="h-4 w-4" />
                  Contact
                </button>
                <button
                  onClick={() => setActiveTab('devis')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-all ${
                    activeTab === 'devis'
                      ? 'bg-[#C4724F] text-white shadow-lg'
                      : 'bg-white text-[#0A2540] hover:bg-[#C4724F]/5 border border-slate-200'
                  }`}
                  data-testid="tab-devis"
                >
                  <FileText className="h-4 w-4" />
                  Demande de devis
                </button>
              </div>

              {/* Contact Form */}
              {activeTab === 'contact' && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 sm:p-8">
                    <h3 className="font-display text-2xl font-semibold text-[#0A2540] mb-6">
                      Envoyez-nous un message
                    </h3>
                    <form onSubmit={handleContactSubmit} className="space-y-5" data-testid="contact-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <FormField label="Nom complet" required icon={User}>
                          <Input name="name" value={contactForm.name} onChange={handleContactChange} placeholder="Jean Dupont" className="pl-10" required data-testid="contact-name-input" />
                        </FormField>
                        <FormField label="Email" required icon={Mail}>
                          <Input name="email" type="email" value={contactForm.email} onChange={handleContactChange} placeholder="jean@exemple.com" className="pl-10" required data-testid="contact-email-input" />
                        </FormField>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <FormField label="T&eacute;l&eacute;phone" icon={Phone}>
                          <Input name="phone" value={contactForm.phone} onChange={handleContactChange} placeholder="+33 6 12 34 56 78" className="pl-10" data-testid="contact-phone-input" />
                        </FormField>
                        <div>
                          <Label>Sujet *</Label>
                          <Select value={contactForm.subject} onValueChange={(v) => setContactForm(prev => ({ ...prev, subject: v }))}>
                            <SelectTrigger className="mt-1" data-testid="contact-subject-select">
                              <SelectValue placeholder="Choisissez un sujet" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="info">Demande d'informations</SelectItem>
                              <SelectItem value="réservation">Question sur une r&eacute;servation</SelectItem>
                              <SelectItem value="partenariat">Partenariat</SelectItem>
                              <SelectItem value="autre">Autre</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Message *</Label>
                        <Textarea name="message" value={contactForm.message} onChange={handleContactChange} placeholder="Votre message..." className="mt-1 min-h-[140px]" required data-testid="contact-message-input" />
                      </div>
                      <SubmitButton submitting={submitting} label="Envoyer le message" />
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Quote Form */}
              {activeTab === 'devis' && (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 sm:p-8">
                    <h3 className="font-display text-2xl font-semibold text-[#0A2540] mb-2">
                      Demande de devis
                    </h3>
                    <p className="text-slate-500 text-sm mb-6">
                      Remplissez ce formulaire et nous vous ferons parvenir un devis personnalis&eacute; sous 48h.
                    </p>
                    <form onSubmit={handleQuoteSubmit} className="space-y-5" data-testid="quote-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <FormField label="Nom complet" required icon={User}>
                          <Input name="customer_name" value={quoteForm.customer_name} onChange={handleQuoteChange} placeholder="Jean Dupont" className="pl-10" required data-testid="quote-name-input" />
                        </FormField>
                        <FormField label="Email" required icon={Mail}>
                          <Input name="customer_email" type="email" value={quoteForm.customer_email} onChange={handleQuoteChange} placeholder="jean@exemple.com" className="pl-10" required data-testid="quote-email-input" />
                        </FormField>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <FormField label="T&eacute;l&eacute;phone" required icon={Phone}>
                          <Input name="customer_phone" value={quoteForm.customer_phone} onChange={handleQuoteChange} placeholder="+33 6 12 34 56 78" className="pl-10" required data-testid="quote-phone-input" />
                        </FormField>
                        <div>
                          <Label>Type de croisi&egrave;re *</Label>
                          <Select value={quoteForm.request_type} onValueChange={(v) => setQuoteForm(prev => ({ ...prev, request_type: v }))}>
                            <SelectTrigger className="mt-1" data-testid="quote-type-select">
                              <SelectValue placeholder="Type de r&eacute;servation" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cabin">R&eacute;servation cabine</SelectItem>
                              <SelectItem value="privatization">Privatisation compl&egrave;te</SelectItem>
                              <SelectItem value="custom">Croisi&egrave;re sur mesure</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <Label>Croisi&egrave;re souhait&eacute;e</Label>
                          <Select value={quoteForm.cruise_name} onValueChange={(v) => setQuoteForm(prev => ({ ...prev, cruise_name: v }))}>
                            <SelectTrigger className="mt-1" data-testid="quote-cruise-select">
                              <SelectValue placeholder="S&eacute;lectionnez (optionnel)" />
                            </SelectTrigger>
                            <SelectContent>
                              {cruises.map(c => (
                                <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                              ))}
                              <SelectItem value="Je ne sais pas encore">Je ne sais pas encore</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <FormField label="Nb de passagers" required icon={Users}>
                          <Input name="passenger_count" type="number" min="1" max="20" value={quoteForm.passenger_count} onChange={handleQuoteChange} placeholder="2" className="pl-10" required data-testid="quote-passengers-input" />
                        </FormField>
                      </div>
                      <FormField label="Dates souhait&eacute;es" required icon={Calendar}>
                        <Input name="preferred_dates" value={quoteForm.preferred_dates} onChange={handleQuoteChange} placeholder="Ex: Juillet 2027, dates flexibles" className="pl-10" required data-testid="quote-dates-input" />
                      </FormField>
                      <div>
                        <Label>Message / Pr&eacute;cisions *</Label>
                        <Textarea name="message" value={quoteForm.message} onChange={handleQuoteChange} placeholder="D&eacute;crivez votre projet de croisi&egrave;re, vos attentes particuli&egrave;res..." className="mt-1 min-h-[120px]" required data-testid="quote-message-input" />
                      </div>
                      <SubmitButton submitting={submitting} label="Envoyer ma demande de devis" accent />
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

/* Reusable sub-components */
const InfoRow = ({ icon: Icon, title, children }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div>
      <h4 className="font-semibold text-sm">{title}</h4>
      <div className="text-white/70 text-sm">{children}</div>
    </div>
  </div>
);

const FormField = ({ label, required, icon: Icon, children }) => (
  <div>
    <Label>{label} {required && '*'}</Label>
    <div className="relative mt-1">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8] pointer-events-none" />}
      {children}
    </div>
  </div>
);

const SubmitButton = ({ submitting, label, accent }) => (
  <Button
    type="submit"
    className={`w-full font-semibold ${accent ? 'bg-[#C4724F] hover:bg-[#C4724F]/90' : 'bg-[#D4AF37] hover:bg-[#D4AF37]/90'} text-white`}
    size="lg"
    disabled={submitting}
    data-testid="contact-submit-btn"
  >
    {submitting ? (
      <span className="flex items-center gap-2">
        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white" />
        Envoi en cours...
      </span>
    ) : (
      <>
        {label}
        <Send className="ml-2 h-5 w-5" />
      </>
    )}
  </Button>
);

export default ContactPage;
